import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Copy, MousePointerClick, DollarSign, BadgePercent, Link2, Target, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { formatPrice } from "@/lib/storefront";

export const Route = createFileRoute("/_authenticated/_account/affiliate")({
  head: () => ({
    meta: [
      { title: "Affiliate Dashboard — CoderLity" },
      { name: "description", content: "Track your referral clicks, commissions, and payouts." },
    ],
  }),
  component: AffiliatePage,
});

type Affiliate = {
  id: string;
  code: string;
  status: "pending" | "active" | "suspended" | "rejected";
  commission_rate: number;
  payout_email: string | null;
  notes: string | null;
  website: string | null;
  country: string | null;
  created_at: string;
  review_reason?: string | null;
};

type Commission = {
  id: string;
  amount_cents: number;
  status: "pending" | "approved" | "paid" | "rejected";
  created_at: string;
  paid_at: string | null;
  order_id: string;
};

const statusVariant: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  active: "default",
  pending: "secondary",
  approved: "default",
  paid: "default",
  suspended: "destructive",
  rejected: "destructive",
};

function AffiliatePage() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: affiliate, isLoading } = useQuery({
    enabled: !!user,
    queryKey: ["my-affiliate", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affiliates")
        .select("id, code, status, commission_rate, payout_email, notes, website, country, created_at, review_reason")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data as Affiliate | null;
    },
  });

  if (isLoading) {
    return (
      <>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </>
    );
  }

  if (!affiliate) return <AffiliateApply />;

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Affiliate dashboard</h1>
          <p className="text-sm text-muted-foreground">Track your referral performance and earnings.</p>
        </div>
        <Badge variant={statusVariant[affiliate.status] ?? "secondary"} className="capitalize">
          {affiliate.status}
        </Badge>
      </div>

      {affiliate.status !== "active" && (
        <Card className="border-dashed">
          <CardContent className="py-4 text-sm text-muted-foreground space-y-2">
            <p>
              Your affiliate application is <strong className="capitalize text-foreground">{affiliate.status}</strong>.
              {affiliate.status === "pending" && " We'll review it shortly."}
              {affiliate.status !== "pending" && " Clicks and commissions are tracked once approved."}
            </p>
            {affiliate.review_reason && (
              <p className="rounded-md border border-border bg-secondary/40 p-3 text-foreground">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">Reviewer note:</span>{" "}
                {affiliate.review_reason}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <AvailableActions affiliate={affiliate} onChanged={() => qc.invalidateQueries({ queryKey: ["my-affiliate"] })} />
      <AffiliateProfile affiliate={affiliate} />
      {affiliate.status === "active" && <AffiliateLink code={affiliate.code} />}
      {affiliate.status === "active" && (
        <StatsAndCommissions affiliateId={affiliate.id} commissionRate={Number(affiliate.commission_rate)} />
      )}
      <PayoutSettings affiliate={affiliate} onSaved={() => qc.invalidateQueries({ queryKey: ["my-affiliate"] })} />
    </>
  );
}

function AvailableActions({ affiliate, onChanged }: { affiliate: Affiliate; onChanged: () => void }) {
  const actions: { label: string; description: string; onClick: () => void; disabled?: boolean; variant?: "default" | "outline" | "secondary" | "destructive" }[] = [];

  const copyLink = () => {
    const url = `${typeof window !== "undefined" ? window.location.origin : ""}/?ref=${affiliate.code}`;
    navigator.clipboard.writeText(url);
    toast.success("Referral link copied");
  };
  const copyCode = () => {
    navigator.clipboard.writeText(affiliate.code);
    toast.success("Referral code copied");
  };
  const scrollTo = (id: string) => () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const resubmit = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("affiliates")
        .update({ status: "pending", review_reason: null })
        .eq("id", affiliate.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Application resubmitted for review");
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (affiliate.status === "pending") {
    actions.push({ label: "Edit application details", description: "Update your payout email while the review is in progress.", onClick: scrollTo("payout-settings"), variant: "outline" });
    actions.push({ label: "View referral code", description: "See the code you'll receive once approved.", onClick: copyCode, variant: "outline" });
  } else if (affiliate.status === "active") {
    actions.push({ label: "Copy referral link", description: "Share your link to earn commissions.", onClick: copyLink });
    actions.push({ label: "View referral code", description: `Your code: ${affiliate.code}`, onClick: copyCode, variant: "outline" });
    actions.push({ label: "Edit payout details", description: "Change where your earnings are sent.", onClick: scrollTo("payout-settings"), variant: "outline" });
  } else if (affiliate.status === "rejected") {
    actions.push({ label: "Resubmit application", description: "Address the reviewer's feedback and submit again.", onClick: () => resubmit.mutate(), disabled: resubmit.isPending });
    actions.push({ label: "Edit details", description: "Update your payout email before resubmitting.", onClick: scrollTo("payout-settings"), variant: "outline" });
  } else if (affiliate.status === "suspended") {
    actions.push({ label: "Contact support", description: "Reach out to appeal your suspension.", onClick: () => { window.location.href = "mailto:support@coderlity.com?subject=Affiliate%20suspension%20appeal"; }, variant: "outline" });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">What you can do now</CardTitle>
        <CardDescription>Available actions for your current affiliate status.</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="grid gap-3 sm:grid-cols-2">
          {actions.map((a) => (
            <li key={a.label} className="flex items-start justify-between gap-3 rounded-md border border-border bg-secondary/30 p-3">
              <div className="min-w-0">
                <div className="text-sm font-medium">{a.label}</div>
                <div className="text-xs text-muted-foreground">{a.description}</div>
              </div>
              <Button size="sm" variant={a.variant ?? "default"} onClick={a.onClick} disabled={a.disabled}>
                {a.label.split(" ")[0]}
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function AffiliateApply() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [payoutEmail, setPayoutEmail] = useState(user?.email ?? "");
  const [website, setWebsite] = useState("");
  const [country, setCountry] = useState("");
  const [notes, setNotes] = useState("");
  const [agreed, setAgreed] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!agreed) {
        throw new Error("Please accept the affiliate terms to continue.");
      }
      if (website && !/^https?:\/\/.+\..+/i.test(website)) {
        throw new Error("Website must be a valid URL (https://example.com)");
      }
      const code = `${(user?.email ?? "ref").split("@")[0].replace(/[^a-z0-9]/gi, "").slice(0, 8).toUpperCase()}${Math.floor(Math.random() * 9000 + 1000)}`;
      const { error } = await supabase.from("affiliates").insert({
        user_id: user!.id,
        code,
        payout_email: payoutEmail || null,
        website: website || null,
        country: country || null,
        notes: notes || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Application submitted — we'll review it shortly.");
      qc.invalidateQueries({ queryKey: ["my-affiliate"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Join the affiliate program</h1>
        <p className="text-sm text-muted-foreground">Earn commission for every customer you refer.</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Apply now</CardTitle>
          <CardDescription>Tell us how you'd like to be paid and a bit about your audience.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="payout">Payout email</Label>
            <Input id="payout" type="email" value={payoutEmail} onChange={(e) => setPayoutEmail(e.target.value)} placeholder="payout@example.com" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input id="website" type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://example.com" maxLength={255} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input id="country" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="United States" maxLength={100} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">About you (optional)</Label>
            <Input id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Audience, channels, ideas…" maxLength={500} />
          </div>
          <label className="flex items-start gap-3 rounded-md border border-border bg-secondary/30 p-3 text-sm cursor-pointer">
            <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} className="mt-0.5" />
            <span className="text-muted-foreground">
              I agree to the{" "}
              <a href="/terms#affiliates" target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2">
                Affiliate Program Terms
              </a>
              , confirm my referrals will follow fair-use and anti-spam guidelines, and accept that fraudulent activity may result in account suspension and forfeiture of commissions.
            </span>
          </label>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || !agreed}>
            {mutation.isPending ? "Submitting…" : "Submit application"}
          </Button>
        </CardContent>
      </Card>
    </>
  );
}

function AffiliateProfile({ affiliate }: { affiliate: Affiliate }) {
  const joined = new Date(affiliate.created_at).toLocaleDateString(undefined, { dateStyle: "medium" });
  const website = affiliate.website;
  const host = website ? website.replace(/^https?:\/\//, "").replace(/\/$/, "") : null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Affiliate profile</CardTitle>
        <CardDescription>Your application details on file.</CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Status</dt>
            <dd className="mt-1">
              <Badge variant={statusVariant[affiliate.status] ?? "secondary"} className="capitalize">
                {affiliate.status}
              </Badge>
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Website</dt>
            <dd className="mt-1 font-medium truncate">
              {website ? (
                <a href={website} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                  {host}
                </a>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Country</dt>
            <dd className="mt-1 font-medium">{affiliate.country || <span className="text-muted-foreground">—</span>}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Joined</dt>
            <dd className="mt-1 font-medium">{joined}</dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}

function AffiliateLink({ code }: { code: string }) {
  const url = typeof window !== "undefined" ? `${window.location.origin}/?ref=${code}` : `/?ref=${code}`;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Link2 className="h-4 w-4 text-primary" /> Your referral link</CardTitle>
        <CardDescription>Share this link — every click and order is attributed to you.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <code className="flex-1 rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm">{url}</code>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => {
              navigator.clipboard.writeText(url);
              toast.success("Copied to clipboard");
            }}
          >
            <Copy className="h-3.5 w-3.5" /> Copy
          </Button>
        </div>
        <div className="text-xs text-muted-foreground">Referral code: <span className="font-mono text-foreground">{code}</span></div>
      </CardContent>
    </Card>
  );
}

function StatsAndCommissions({ affiliateId, commissionRate }: { affiliateId: string; commissionRate: number }) {
  const { data: clickCount } = useQuery({
    queryKey: ["aff-clicks", affiliateId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("affiliate_clicks")
        .select("*", { count: "exact", head: true })
        .eq("affiliate_id", affiliateId);
      if (error) throw error;
      return count ?? 0;
    },
  });

  const { data: commissions } = useQuery({
    queryKey: ["aff-commissions", affiliateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affiliate_commissions")
        .select("id, amount_cents, status, created_at, paid_at, order_id")
        .eq("affiliate_id", affiliateId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Commission[];
    },
  });

  const totals = (commissions ?? []).reduce(
    (acc, c) => {
      if (c.status === "paid") acc.paid += c.amount_cents;
      else if (c.status === "rejected") acc.rejected += c.amount_cents;
      else acc.pending += c.amount_cents;
      return acc;
    },
    { paid: 0, pending: 0, rejected: 0 },
  );

  const conversions = commissions?.length ?? 0;
  const totalEarned = totals.paid + totals.pending;
  const conversionRate = clickCount && clickCount > 0 ? (conversions / clickCount) * 100 : 0;

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard icon={MousePointerClick} label="Total clicks" value={String(clickCount ?? 0)} />
        <StatCard icon={Target} label="Conversions" value={String(conversions)} />
        <StatCard icon={TrendingUp} label="Conversion rate" value={`${conversionRate.toFixed(1)}%`} />
        <StatCard icon={BadgePercent} label="Commission rate" value={`${commissionRate}%`} />
        <StatCard icon={DollarSign} label="Pending earnings" value={formatPrice(totals.pending)} />
        <StatCard icon={DollarSign} label="Total earned" value={formatPrice(totalEarned)} accent />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Commissions</CardTitle>
          <CardDescription>Earnings from referred orders.</CardDescription>
        </CardHeader>
        <CardContent>
          {!commissions || commissions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No commissions yet. Share your link to get started.</p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {commissions.map((c) => (
                <li key={c.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-medium">Order #{c.order_id.slice(0, 8).toUpperCase()}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(c.created_at).toLocaleDateString(undefined, { dateStyle: "medium" })}
                      {c.paid_at ? ` · paid ${new Date(c.paid_at).toLocaleDateString()}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={statusVariant[c.status] ?? "secondary"} className="capitalize">{c.status}</Badge>
                    <div className="w-24 text-right font-semibold">{formatPrice(c.amount_cents)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function StatCard({ icon: Icon, label, value, accent }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string; accent?: boolean }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 py-5">
        <div className={`rounded-md p-2 ${accent ? "bg-gradient-brand/20 text-primary" : "bg-secondary/60 text-muted-foreground"}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="truncate text-lg font-semibold">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function PayoutSettings({ affiliate, onSaved }: { affiliate: Affiliate; onSaved: () => void }) {
  const [email, setEmail] = useState(affiliate.payout_email ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("affiliates")
      .update({ payout_email: email || null })
      .eq("id", affiliate.id);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Payout email updated");
    onSaved();
  };

  return (
    <Card id="payout-settings">
      <CardHeader>
        <CardTitle>Payout settings</CardTitle>
        <CardDescription>Where we'll send your earnings.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="payout-email">Payout email</Label>
          <Input id="payout-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="payout@example.com" />
        </div>
        <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
      </CardContent>
    </Card>
  );
}
